import { useState } from 'react';
import Split from 'react-split';
import {
  AritoSplitPane,
  SidebarButton,
  ErpDataTableV2,
  AritoIcon,
  ExcelImportDialog,
  ExcelImportErrorDialog
} from '@/components/custom/erp';
import { getPermissionResourceKeyFromPath } from '@/lib/permissions';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { ExcelConfig, ImportBody } from './utils/excel-util';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { itemGroupColumns } from './cols-definition';
import type { ItemGroupFormValues } from './schemas';
import { toast as hotToast } from '@/lib/hot-toast';
import { toast, useExcelProcessor } from '@/hooks';
import { useAuth } from '@/contexts/auth-context';
import { useData, useFormState } from './hooks';
import { GroupType } from '@/types/schemas';
import { useGroup } from '@/hooks/queries';

export const groupTypes = [
  {
    label: 'Nhóm vật tư 1 (VT1)',
    value: GroupType.MATERIAL1,
    shortCode: 'VT1'
  },
  {
    label: 'Nhóm vật tư 2 (VT2)',
    value: GroupType.MATERIAL2,
    shortCode: 'VT2'
  },
  {
    label: 'Nhóm vật tư 3 (VT3)',
    value: GroupType.MATERIAL3,
    shortCode: 'VT3'
  }
];
const DEFAULT_GROUP = groupTypes[0].value;

export default function ItemGroupPage() {
  const {
    groups: groupsVT1,
    isLoading: isLoadingVT1,
    refreshGroups: refreshGroupsVT1,
    fetchGroupsByType: fetchGroupsByTypeVT1,
    addGroup: addGroupVT1,
    updateGroup: updateGroupVT1,
    deleteGroup: deleteGroupVT1
  } = useGroup(GroupType.MATERIAL1);

  const {
    groups: groupsVT2,
    isLoading: isLoadingVT2,
    refreshGroups: refreshGroupsVT2,
    fetchGroupsByType: fetchGroupsByTypeVT2,
    addGroup: addGroupVT2,
    updateGroup: updateGroupVT2,
    deleteGroup: deleteGroupVT2
  } = useGroup(GroupType.MATERIAL2);

  const {
    groups: groupsVT3,
    isLoading: isLoadingVT3,
    refreshGroups: refreshGroupsVT3,
    fetchGroupsByType: fetchGroupsByTypeVT3,
    addGroup: addGroupVT3,
    updateGroup: updateGroupVT3,
    deleteGroup: deleteGroupVT3
  } = useGroup(GroupType.MATERIAL3);

  const getGroupFunctions = (groupType: GroupType) => {
    switch (groupType) {
      case GroupType.MATERIAL1:
        return {
          groups: groupsVT1,
          refreshGroups: refreshGroupsVT1,
          fetchGroupsByType: fetchGroupsByTypeVT1,
          addGroup: addGroupVT1,
          updateGroup: updateGroupVT1,
          deleteGroup: deleteGroupVT1,
          isLoading: isLoadingVT1
        };
      case GroupType.MATERIAL2:
        return {
          groups: groupsVT2,
          refreshGroups: refreshGroupsVT2,
          fetchGroupsByType: fetchGroupsByTypeVT2,
          addGroup: addGroupVT2,
          updateGroup: updateGroupVT2,
          deleteGroup: deleteGroupVT2,
          isLoading: isLoadingVT2
        };
      case GroupType.MATERIAL3:
        return {
          groups: groupsVT3,
          refreshGroups: refreshGroupsVT3,
          fetchGroupsByType: fetchGroupsByTypeVT3,
          addGroup: addGroupVT3,
          updateGroup: updateGroupVT3,
          deleteGroup: deleteGroupVT3,
          isLoading: isLoadingVT3
        };
      default:
        return {
          groups: groupsVT1,
          refreshGroups: refreshGroupsVT1,
          fetchGroupsByType: fetchGroupsByTypeVT1,
          addGroup: addGroupVT1,
          updateGroup: updateGroupVT1,
          deleteGroup: deleteGroupVT1,
          isLoading: isLoadingVT1
        };
    }
  };

  const { activeGroup, handleFilter } = useData(DEFAULT_GROUP);

  const { isLoading, addGroup, updateGroup, deleteGroup, groups, refreshGroups } = getGroupFunctions(activeGroup);

  const { permissions, isAdmin } = useAuth();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const { processFile } = useExcelProcessor(ExcelConfig);

  const handleOpenImportExcel = () => {
    const getPermission = (action: 'create' | 'update' | 'delete') => {
      if (isAdmin) return true;
      const resourceKey = getPermissionResourceKeyFromPath(window.location.pathname);
      return resourceKey ? permissions[resourceKey]?.[action] === true : false;
    };

    if (!getPermission('create')) {
      hotToast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setIsImportDialogOpen(true);
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addGroup({
          ...body,
          loai_nhom: activeGroup
        });
      }
      await refreshGroups();
      toast({
        description: 'Đã nhập dữ liệu nhóm vật tư từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useFormState(activeGroup);

  const tables = [
    {
      name: '',
      rows: groups,
      columns: itemGroupColumns,
      key: `${activeGroup}-${Date.now()}`
    }
  ];

  const handleFormSubmit = async (data: ItemGroupFormValues) => {
    try {
      const formData = {
        ...data,
        loai_nhom: activeGroup
      };

      if (formMode === 'add') {
        await addGroup(formData);
        await refreshGroups();
      } else if (formMode === 'edit' && selectedObj) {
        await updateGroup(selectedObj.uuid, formData);
        await refreshGroups();
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <div className='flex h-screen w-full min-w-0 max-w-full flex-row overflow-hidden bg-white'>
      <div className={`h-full min-w-0 flex-1 ${showForm ? 'hidden' : ''}`}>
        <AritoSplitPane split='vertical' minSize={200} defaultSize={300}>
          <div className='h-full overflow-y-auto border-r'>
            <div className='p-4'>
              <h2 className='mb-4 flex items-center gap-1 text-lg font-semibold'>
                <AritoIcon icon={539} /> Danh mục phân nhóm
              </h2>
              <hr className='mb-4' />
              <ul>
                {groupTypes.map(group => (
                  <li key={group.value} className='mb-2 text-sm'>
                    <SidebarButton isActive={activeGroup === group.value} onClick={() => handleFilter(group.value)}>
                      {group.label}
                    </SidebarButton>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className='h-full min-w-0 overflow-hidden'>
            <div className='flex h-full min-w-0 flex-col'>
              <ActionBar
                onAddClick={handleAddClick}
                onEditClick={() => selectedObj && handleEditClick()}
                onDeleteClick={() => selectedObj && handleDeleteClick()}
                onCopyClick={() => selectedObj && handleCopyClick()}
                onViewClick={() => selectedObj && handleViewClick()}
                isViewDisabled={!selectedObj}
                activeGroup={activeGroup}
                onRefresh={() => refreshGroups()}
                onImportFromExcel={handleOpenImportExcel}
              />

              <div className='h-full w-full min-w-0 flex-1 overflow-hidden'>
                <ErpDataTableV2
                  key={activeGroup}
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  getRowId={row => row.uuid}
                  loading={isLoading}
                />
              </div>
            </div>
          </div>
        </AritoSplitPane>
      </div>

      {showForm && (
        <FormDialog
          open={showForm}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onDeleteButtonClick={handleDeleteClick}
          onCopyButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : undefined
          }
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          selectedObj={selectedObj}
          onClose={handleCloseDelete}
          deleteGroup={deleteGroup}
          clearSelection={clearSelection}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}
